import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Badge,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  AccessTime,
  EventNote,
  Person,
  Notifications,
  Work,
  CalendarMonth,
  People,
  Logout,
  Assessment,
  EventAvailable,
  Settings,
  Assignment,
  Receipt,
  Business,
  ExpandLess,
  ExpandMore,
  Category,
  Home,
  Menu,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { leaveAPI, notificationsAPI } from '../services/api';
import { motion } from 'framer-motion';

const DRAWER_WIDTH = 280;

export const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'Attendance', icon: <AccessTime />, path: '/attendance' },
  { text: 'Leave', icon: <EventNote />, path: '/leave' },
  { text: 'Payslip', icon: <Receipt />, path: '/payslip' },
  { text: 'Tasks', icon: <Assignment />, path: '/tasks' },
  { text: 'Reports', icon: <Assessment />, path: '/reports' },
  { text: 'Calendar', icon: <EventAvailable />, path: '/calendar' },
  { 
    text: 'Holidays', 
    icon: <CalendarMonth />, 
    isDropdown: true,
    subItems: [
      { text: 'Holiday Categories', icon: <Category />, path: '/holiday-categories' },
    ]
  },
];

export const adminMenuItems = [
  { text: 'Admin Dashboard', icon: <Dashboard />, path: '/admin' },
  { text: 'Employees', icon: <People />, path: '/admin/employees' },
  { text: 'Companies', icon: <Business />, path: '/admin/companies' },
  { text: 'Shifts', icon: <Work />, path: '/admin/shifts' },
  { 
    text: 'Holidays', 
    icon: <CalendarMonth />, 
    isDropdown: true,
    subItems: [
      { text: 'Holiday Categories', icon: <Category />, path: '/admin/holiday-categories' },
      { text: 'Manage Holidays', icon: <CalendarMonth />, path: '/admin/holidays' },
      { text: 'Holiday Approvals', icon: <EventAvailable />, path: '/admin/holiday-approvals' },
    ]
  },
  {
    text: 'Leaves',
    icon: <EventNote />,
    isDropdown: true,
    subItems: [
      { text: 'My Leaves', icon: <EventNote />, path: '/leave' },
      { text: 'Leave Requests', icon: <EventNote />, path: '/admin/leave-requests', badge: true },
    ]
  },
  { text: 'Payslip Details', icon: <Receipt />, path: '/admin/payslip-details' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

const Sidebar = ({ open, onClose, onToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [holidaysDropdownOpen, setHolidaysDropdownOpen] = useState(false);
  const [leavesDropdownOpen, setLeavesDropdownOpen] = useState(false);
  
  const notificationBadgeCount = (user?.role === 'admin' || user?.role === 'hr') 
    ? unreadNotificationCount + pendingLeaveCount 
    : unreadNotificationCount;

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (user?.role === 'admin' || user?.role === 'hr') {
        try {
          const response = await leaveAPI.getPendingCount();
          if (response.data.success) {
            setPendingLeaveCount(response.data.data.count);
          }
        } catch (error) {
          console.error('Error fetching pending leave count:', error);
        }
      }
    };
    
    const fetchUnreadNotifications = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        if (response.data.success) {
          setUnreadNotificationCount(response.data.data.count);
        }
      } catch (error) {
        console.error('Error fetching unread notification count:', error);
      }
    };
    
    fetchPendingCount();
    fetchUnreadNotifications();
    
    const interval = setInterval(() => {
      fetchPendingCount();
      fetchUnreadNotifications();
    }, 60000);
    
    return () => clearInterval(interval);
  }, [user]);

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const renderMenuItem = (item, isSubItem = false) => (
    <ListItem key={item.path || item.text} disablePadding>
      <ListItemButton
        onClick={() => item.path && handleNavigation(item.path)}
        sx={{
          py: 1.4,
          px: 2,
          borderRadius: 3,
          mx: 1,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { 
            bgcolor: 'rgba(212, 175, 55, 0.12)',
            transform: 'translateX(4px)',
          },
          bgcolor: isActive(item.path || '') ? 'rgba(212, 175, 55, 0.15)' : 'transparent',
          borderLeft: isActive(item.path || '') ? '3px solid #D4AF37' : '3px solid transparent',
        }}
      >
        <ListItemIcon sx={{ 
          color: isActive(item.path || '') ? '#D4AF37' : '#8AA87D',
          minWidth: isSubItem ? 36 : 40,
          transition: 'all 0.3s ease',
        }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText 
          primary={item.text} 
          sx={{ 
            '& .MuiTypography-root': { 
              color: isActive(item.path || '') ? '#D4AF37' : '#B0BEC5',
              fontWeight: isActive(item.path || '') ? 700 : 500,
              letterSpacing: '0.3px',
              fontSize: isSubItem ? '0.875rem' : '0.9rem',
            } 
          }} 
        />
        {item.badge && (
          <Badge 
            badgeContent={notificationBadgeCount}
            sx={{
              '& .MuiBadge-badge': {
                background: 'linear-gradient(135deg, #EF5350, #C62828)',
                color: '#FFF',
                fontSize: '0.65rem',
                fontWeight: 700,
                minWidth: 20,
                height: 20,
                borderRadius: '10px',
              }
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );

  const renderDropdown = (item, dropdownOpen, setDropdownOpen) => (
    <Box key={item.text}>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => setDropdownOpen(!dropdownOpen)}
          sx={{
            py: 1.4,
            px: 2,
            borderRadius: 3,
            mx: 1,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { 
              bgcolor: 'rgba(212, 175, 55, 0.12)',
              transform: 'translateX(4px)',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: '#8AA87D',
            minWidth: 40,
          }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText 
            primary={item.text} 
            sx={{ 
              '& .MuiTypography-root': { 
                color: '#B0BEC5',
                fontWeight: 500,
                letterSpacing: '0.3px',
              } 
            }} 
          />
          <motion.div
            animate={{ rotate: dropdownOpen ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ExpandMore sx={{ color: '#8AA87D' }} />
          </motion.div>
        </ListItemButton>
      </ListItem>
      <Collapse in={dropdownOpen} timeout={300}>
        <List disablePadding sx={{ pl: 2 }}>
          {item.subItems.map((subItem) => renderMenuItem(subItem, true))}
        </List>
      </Collapse>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={isMobile ? onClose : undefined}
      ModalProps={isMobile ? { keepMounted: true } : undefined}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #0F2744 0%, #0A1929 100%)',
          borderRight: '1px solid rgba(212, 175, 55, 0.15)',
          boxShadow: isMobile ? '8px 0 40px rgba(0,0,0,0.5)' : '4px 0 24px rgba(0,0,0,0.3)',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(212, 175, 55, 0.3)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(212, 175, 55, 0.5)',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        justifyContent: 'flex-end',
        borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
      }}>
        <IconButton
          onClick={onToggle}
          sx={{
            color: '#D4AF37',
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(212, 175, 55, 0.15)',
              transform: 'rotate(90deg)',
            },
          }}
        >
          <Menu />
        </IconButton>
      </Box>

      {/* User Profile */}
      <Box 
        sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          background: 'linear-gradient(135deg, rgba(212,175,55,0.05) 0%, transparent 100%)',
        }}
      >
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(212, 175, 55, 0.15)',
            color: '#D4AF37', 
            width: 52, 
            height: 52,
            fontWeight: 700,
            fontSize: '1.1rem',
            border: '2px solid rgba(212, 175, 55, 0.4)',
            transition: 'all 0.3s ease',
            '&:hover': {
              border: '2px solid #D4AF37',
              boxShadow: '0 0 20px rgba(212,175,55,0.3)',
            },
          }}
        >
          {user?.first_name?.[0]}{user?.last_name?.[0]}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 700, 
            color: '#D4AF37',
            letterSpacing: '0.5px',
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {user?.first_name} {user?.last_name}
          </Typography>
          <Typography variant="caption" sx={{ 
            color: '#8AA87D',
            fontWeight: 600,
            letterSpacing: '1px',
            fontSize: '0.7rem',
          }}>
            {user?.role?.toUpperCase()}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ 
        borderColor: 'rgba(212, 175, 55, 0.1)',
        mx: 2,
      }} />

      {/* Employee Menu */}
      <List sx={{ px: 1, py: 1.5 }}>
        {menuItems.map((item) => (
          item.isDropdown 
            ? renderDropdown(item, holidaysDropdownOpen, setHolidaysDropdownOpen)
            : renderMenuItem(item)
        ))}
      </List>

      {/* Admin/HR Section */}
      {(user?.role === 'admin' || user?.role === 'hr') && (
        <>
          <Divider sx={{ 
            borderColor: 'rgba(212, 175, 55, 0.1)',
            mx: 3,
            my: 1,
          }} />
          <Typography variant="caption" sx={{ 
            px: 3, 
            color: '#8AA87D', 
            display: 'block', 
            mb: 0.5,
            fontWeight: 700,
            letterSpacing: '1.5px',
            fontSize: '0.65rem',
            textTransform: 'uppercase',
          }}>
            Administration
          </Typography>
          <List sx={{ px: 1, py: 0.5 }}>
            {adminMenuItems.map((item) => (
              item.isDropdown 
                ? (item.text === 'Holidays' 
                    ? renderDropdown(item, holidaysDropdownOpen, setHolidaysDropdownOpen)
                    : renderDropdown(item, leavesDropdownOpen, setLeavesDropdownOpen))
                : renderMenuItem(item)
            ))}
          </List>
        </>
      )}

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Logout */}
      <Divider sx={{ 
        borderColor: 'rgba(212, 175, 55, 0.1)',
        mx: 3,
      }} />
      <List sx={{ px: 1, py: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              py: 1.4,
              px: 2,
              borderRadius: 3,
              mx: 1,
              transition: 'all 0.3s ease',
              '&:hover': { 
                bgcolor: 'rgba(239, 83, 80, 0.1)',
                transform: 'translateX(4px)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#EF5350', minWidth: 40 }}>
              <Logout />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              sx={{ 
                '& .MuiTypography-root': { 
                  color: '#EF5350',
                  fontWeight: 600,
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
      </List>

      {/* Version */}
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: 'rgba(212, 175, 55, 0.2)', fontSize: '0.6rem', letterSpacing: 0.5 }}>
          Attendance System v2.0
        </Typography>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
export { DRAWER_WIDTH };